/*
 * Copyright (c) 2025 Hardfin, Inc.
 */
import {
  hubspotCompanyPropertyQueryKey,
  hubspotDealPropertyQueryKey,
  hubspotProductPropertyQueryKey,
  salesforceCompanyPropertyQueryKey,
  salesforceDealPropertyQueryKey,
  salesforceProductPropertyQueryKey,
} from '@/account/api/crm-property-mapping';
import { objectTypeTitleMap } from '@/account/api/labels';
import { CRMConfiguration, Organization } from '@/account/api/types';
import { CRMPipelineConfiguration } from '@/account/components/crm-pipeline-configuration';
import CRMPropertyMapping from '@/account/components/crm-property-mapping';
import { LoadingIndicator } from '@/components/navigation-layout';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { CheckCircle, HighlightOff } from '@/constants/hardfin-standard-icons';
import { Box, Tab, Tabs } from '@mui/material';
import {
  ReactNode,
  Suspense,
  SyntheticEvent,
  useEffect,
  useState,
} from 'react';

interface Props {
  organization: Organization;
  integrationConfiguration: CRMConfiguration;
  defaultTab?: number;
  onClose: () => void;
}

const loadingIndicatorSx = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  margin: 'auto',
};

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      sx={{ width: '100%', height: '100%' }}
      {...other}
    >
      {value === index && (
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            width: '100%',
            padding: '0 24px',
            height: '100%',
            overflow: 'auto',
          }}
        >
          <SupportErrorBoundary message="An error occurred while loading this configuration.">
            <Suspense fallback={<LoadingIndicator sx={loadingIndicatorSx} />}>
              {children}
            </Suspense>
          </SupportErrorBoundary>
        </Box>
      )}
    </Box>
  );
}

export function CRMConfigDialog(props: Props) {
  const [currentTab, setCurrentTab] = useState(0);
  useEffect(() => {
    if (props.defaultTab !== undefined && props.defaultTab !== null) {
      setCurrentTab(props.defaultTab);
    }
  }, [props.defaultTab]);
  const handleChange = (_: SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };
  const offIcon = <HighlightOff color="action" fontSize="small" />;
  const onIcon = <CheckCircle color="success" fontSize="small" />;
  const crmStateConfig = props.integrationConfiguration.configurationState;

  // Helper function to safely access steps from both CRM types
  const getStepCompletion = (
    stepId: 'PROJECT' | 'PRODUCT' | 'COMPANY' | 'PIPELINE'
  ): boolean => {
    if ('steps' in crmStateConfig) {
      return crmStateConfig.steps[stepId].completed;
    }
    // Fallback for configurations without steps
    return false;
  };

  // Determine query keys based on CRM type
  const getQueryKeys = () => {
    if (props.integrationConfiguration.type === 'HUBSPOT') {
      return {
        projectQueryKey: hubspotDealPropertyQueryKey,
        productQueryKey: hubspotProductPropertyQueryKey,
        companyQueryKey: hubspotCompanyPropertyQueryKey,
      };
    } else if (props.integrationConfiguration.type === 'SFDC') {
      return {
        projectQueryKey: salesforceDealPropertyQueryKey,
        productQueryKey: salesforceCompanyPropertyQueryKey,
        companyQueryKey: salesforceProductPropertyQueryKey,
      };
    }
    // Default fallback
    return {
      projectQueryKey: [] as string[],
      productQueryKey: [] as string[],
      companyQueryKey: [] as string[],
    };
  };

  const queryKeys = getQueryKeys();
  return (
    <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      <Tabs
        orientation="vertical"
        value={currentTab}
        variant="scrollable"
        scrollButtons="auto"
        onChange={handleChange}
        sx={{
          borderRight: 1,
          borderColor: 'divider',
          minWidth: '200px',
          maxHeight: '100%',
          overflow: 'auto',
          alignItems: 'left',
          '& .MuiTab-root': {
            padding: '12px 16px',
          },
          '& .Mui-selected': {
            backgroundColor: '#eeeeee',
          },
          '& .MuiButtonBase-root.MuiTab-root': {
            textAlign: 'left',
            justifyContent: 'flex-start',
            alignItems: 'middle',
            marginLeft: 0.5,
            minHeight: 'auto',
          },
        }}
      >
        <Tab
          label={objectTypeTitleMap['PROJECT']}
          icon={getStepCompletion('PROJECT') ? onIcon : offIcon}
          iconPosition="start"
        />
        <Tab
          label={objectTypeTitleMap['PRODUCT']}
          icon={getStepCompletion('PRODUCT') ? onIcon : offIcon}
          iconPosition="start"
        />
        <Tab
          label={objectTypeTitleMap['COMPANY']}
          icon={getStepCompletion('COMPANY') ? onIcon : offIcon}
          iconPosition="start"
        />
        <Tab
          label={objectTypeTitleMap['PIPELINE']}
          icon={getStepCompletion('PIPELINE') ? onIcon : offIcon}
          iconPosition="start"
        />
      </Tabs>

      <Box sx={{ flex: 1, overflow: 'auto', maxHeight: '100%' }}>
        <TabPanel value={currentTab} index={0}>
          <CRMPropertyMapping
            pageID="PROJECT"
            orgKey={props.organization.key}
            onClose={props.onClose}
            queryKey={queryKeys.projectQueryKey}
          />
        </TabPanel>
        <TabPanel value={currentTab} index={1}>
          <CRMPropertyMapping
            pageID="PRODUCT"
            orgKey={props.organization.key}
            onClose={props.onClose}
            queryKey={queryKeys.productQueryKey}
          />
        </TabPanel>
        <TabPanel value={currentTab} index={2}>
          <CRMPropertyMapping
            pageID="COMPANY"
            orgKey={props.organization.key}
            onClose={props.onClose}
            queryKey={queryKeys.companyQueryKey}
          />
        </TabPanel>
        <TabPanel value={currentTab} index={3}>
          <CRMPipelineConfiguration onClose={props.onClose} />
        </TabPanel>
      </Box>
    </Box>
  );
}
